import React, { useState } from 'react';
import { HelpCircle, ChevronDown, ChevronUp, Trophy, Users, Layout, Zap, Info, FileUp, UserPlus, Star, Settings } from 'lucide-react';

const FAQItem = ({ question, answer, icon: Icon }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      marginBottom: '15px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden',
      transition: 'all 0.2s'
    }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <div style={{
            padding: '8px',
            backgroundColor: '#ebf8ff',
            borderRadius: '8px',
            color: '#3182ce'
          }}>
            <Icon size={20} />
          </div>
          <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#2d3748' }}>{question}</span>
        </div>
        {isOpen ? <ChevronUp size={20} color="#a0aec0" /> : <ChevronDown size={20} color="#a0aec0" />}
      </button>
      
      {isOpen && (
        <div style={{
          padding: '0 20px 20px 63px',
          color: '#4a5568',
          lineHeight: '1.6',
          fontSize: '15px'
        }}>
          {answer}
        </div>
      )}
    </div>
  );
};

export default function FAQ({ onNavigate }) {
  const faqs = [
    {
      question: "Comment créer mon premier tournoi ?",
      icon: Trophy,
      answer: "Rendez-vous sur l'onglet 'Accueil'. Remplissez le formulaire 'Nouvel évènement' avec le nom, le lieu et la date, puis validez. Votre compétition apparaîtra dans la liste de vos tournois."
    },
    {
      question: "Comment ajouter des athlètes ?",
      icon: Users,
      answer: "Une fois votre compétition créée et sélectionnée, allez dans l'onglet 'Catégories'. Créez d'abord vos catégories (ex: M80, F65), puis cliquez sur une catégorie pour l'ouvrir. Vous pourrez alors ajouter les tireurs un par un ou par groupe."
    },
    {
      question: "Puis-je importer un fichier de la base de données fédérale ?",
      icon: FileUp,
      answer: (
        <div>
          <p>Oui ! Allez dans l'onglet <strong>Catégories</strong> et utilisez le bouton <strong>Importer CSV/Excel</strong>. Le système accepte les formats <code>.csv</code> et <code>.xlsx</code> / <code>.xls</code>.</p>
          <p>Votre fichier doit contenir les colonnes suivantes (la casse et les accents sont ignorés) :</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', margin: '10px 0', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#ebf8ff' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', border: '1px solid #bee3f8' }}>Colonne</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', border: '1px solid #bee3f8' }}>Description</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', border: '1px solid #bee3f8' }}>Obligatoire</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['nom', 'Nom de famille de l\'athlète', 'Oui'],
                ['prenom', 'Prénom de l\'athlète', 'Oui'],
                ['poids', 'Catégorie de poids (ex: M80, F65…)', 'Oui'],
                ['club', 'Club ou instance de l\'athlète', 'Non'],
              ].map(([col, desc, req]) => (
                <tr key={col}>
                  <td style={{ padding: '7px 12px', border: '1px solid #e2e8f0', fontFamily: 'monospace', fontWeight: 'bold' }}>{col}</td>
                  <td style={{ padding: '7px 12px', border: '1px solid #e2e8f0' }}>{desc}</td>
                  <td style={{ padding: '7px 12px', border: '1px solid #e2e8f0', color: req === 'Oui' ? '#e53e3e' : '#38a169', fontWeight: 'bold' }}>{req}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>Les catégories sont créées automatiquement si elles n'existent pas, et les doublons (même nom + même club) sont ignorés.</p>
        </div>
      )
    },
    {
      question: "Comment ajouter un co-gestionnaire à ma compétition ?",
      icon: UserPlus,
      answer: (
        <div>
          <p>En tant que propriétaire d'une compétition, vous pouvez autoriser d'autres personnes à la gérer. Allez dans l'onglet <strong>Infos</strong> de votre compétition, section <strong>Gestionnaires de la compétition</strong>.</p>
          <p>Saisissez l'adresse e-mail de la personne et cliquez sur <strong>Ajouter</strong>. Si cette personne possède un compte SavatEvent, elle recevra une notification par e-mail.</p>
          <p>Un co-gestionnaire peut :</p>
          <ul>
            <li>Modifier les informations de la compétition</li>
            <li>Gérer les catégories et les athlètes</li>
            <li>Configurer et valider les poules et les matchs</li>
            <li>Saisir les résultats en direct</li>
          </ul>
          <p><strong>Seule la suppression</strong> de la compétition reste réservée au propriétaire.</p>
        </div>
      )
    },
    {
      question: "Comment utiliser le classement pour séparer les têtes de série ?",
      icon: Star,
      answer: (
        <div>
          <p>Si votre compétition dispose d'un classement de l'année précédente, vous pouvez l'utiliser pour garantir que les deux meilleurs athlètes de chaque catégorie ne se retrouvent pas dans la même poule.</p>
          <p><strong>Étape 1 — Activer l'option :</strong> Dans <strong>Config compétition</strong>, activez le toggle <em>Respecter le classement (têtes de série)</em> et sauvegardez.</p>
          <p><strong>Étape 2 — Définir les têtes de série :</strong> Dans l'onglet <strong>Catégories</strong>, cliquez sur l'étoile (☆) à côté d'un athlète pour lui attribuer le rang <strong>#1</strong> ou <strong>#2</strong>.</p>
          <p><strong>Résultat lors de la génération des poules :</strong></p>
          <ul>
            <li>La tête de série <strong>#1</strong> est placée en Poule A, la <strong>#2</strong> en dernière poule.</li>
            <li>Si les poules ont des tailles différentes, les têtes de série sont placées dans les plus petites poules.</li>
            <li>Les deux meilleures têtes de série ne peuvent ainsi se rencontrer qu'en <strong>finale</strong>.</li>
          </ul>
          <p>Les badges <strong>#1</strong> et <strong>#2</strong> sont visibles dans la section Matchs pour vérifier rapidement la répartition.</p>
        </div>
      )
    },
    {
      question: "Comment configurer la répartition des poules sur les rings ?",
      icon: Settings,
      answer: (
        <div>
          <p>Dans <strong>Config compétition</strong>, deux options contrôlent la répartition :</p>
          <ul>
            <li>
              <strong>Poules d'une même catégorie sur la même enceinte</strong> — activé par défaut. Toutes les poules d'une catégorie se disputent sur le même ring. Pratique pour suivre une catégorie, mais peut créer un déséquilibre entre rings si les catégories sont de tailles très différentes.
            </li>
            <li style={{ marginTop: '8px' }}>
              <strong>Désactivé</strong> — chaque poule est affectée indépendamment au ring le moins chargé. Meilleur équilibre du nombre de matchs par ring, mais les poules d'une même catégorie peuvent être sur des rings différents.
            </li>
          </ul>
          <p>Dans les deux cas, l'objectif est de minimiser l'écart de matchs entre les rings. L'option agit uniquement sur la contrainte de regroupement par catégorie.</p>
        </div>
      )
    },
    {
      question: "Comment organiser les matchs sur les rings ?",
      icon: Layout,
      answer: "Allez dans l'onglet 'Matchs'. Dans la sous-section 'Poules', choisissez le nombre de rings (plateaux) souhaité, puis cliquez sur 'Générer la répartition'. Vous pouvez déplacer les catégories d'un ring à l'autre par glisser-déposer. Une fois satisfait, cliquez sur 'Valider le Plateau'."
    },
    {
      question: "Comment lancer le direct (Live) ?",
      icon: Zap,
      answer: "Une fois les matchs générés, allez dans l'onglet 'Direct'. Cliquez sur 'DÉMARRER' pour activer la session. Vous pourrez alors désigner les vainqueurs de chaque combat en temps réel. Les spectateurs verront les mises à jour instantanément sur le lien public."
    },
    {
      question: "Pourquoi mes matchs ne sont pas dans l'ordre ?",
      icon: Info,
      answer: "Le système utilise une logique de répartition intelligente (FIFO par vague) pour garantir un temps de repos optimal aux athlètes. Cependant, vous pouvez réorganiser manuellement l'ordre des combats dans l'onglet 'Matchs' > 'Matchs' par simple glisser-déposer."
    },
    {
        question: "Comment partager les résultats au public ?",
        icon: HelpCircle,
        answer: "Assurez-vous que votre compétition est réglée sur 'Public' dans l'onglet 'Configuration'. Les spectateurs peuvent alors trouver votre tournoi dans la liste des 'Compétitions Publiques' sans avoir besoin de compte."
    }
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <HelpCircle size={48} color="#3182ce" style={{ marginBottom: '15px' }} />
        <h1 style={{ color: '#1a202c', margin: '0 0 10px 0' }}>Centre d'aide</h1>
        <p style={{ color: '#718096', fontSize: '16px' }}>Tout ce que vous devez savoir pour organiser votre tournoi avec SavatEvent</p>
      </div>

      <div style={{ marginBottom: '40px' }}>
        {faqs.map((faq, index) => (
          <FAQItem key={index} {...faq} />
        ))}
      </div>

      <div style={{
        backgroundColor: '#ebf8ff',
        padding: '25px',
        borderRadius: '15px',
        border: '1px solid #bee3f8',
        textAlign: 'center'
      }}>
        <h3 style={{ color: '#2c5282', marginTop: 0 }}>Besoin d'une aide supplémentaire ?</h3>
        <p style={{ color: '#2c5282', marginBottom: '20px' }}>Notre équipe est à votre disposition pour vous accompagner dans l'organisation de vos évènements.</p>
        <button 
          style={{
            backgroundColor: '#3182ce',
            color: 'white',
            padding: '10px 25px',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
          onClick={() => onNavigate && onNavigate('contact')}
        >
          Nous contacter
        </button>
      </div>
    </div>
  );
}
